<app-menu>
  <div class="fsc-shell" [attr.data-theme]="theme">
    <div class="mx-auto flex max-w-[1800px] flex-col gap-4 p-4 lg:p-6">
      <header class="fsc-panel flex flex-wrap items-center justify-between gap-4 p-5">
        <div>
          <div class="fsc-eyebrow">Financials / Rate manager</div>
          <h1 class="m-0 mt-1 text-2xl font-bold">Fuel surcharges</h1>
          <p class="fsc-muted m-0 mt-2 text-sm">
            Manage default percentages, contact rules and index-based schedules.
          </p>
        </div>
        <div class="flex flex-wrap gap-2">
          <button
            class="fsc-button"
            type="button"
            (click)="toggleTheme()"
            [attr.aria-label]="'Switch to ' + (theme === 'dark' ? 'light' : 'dark') + ' theme'"
          >
            <i
              class="fa-solid"
              [ngClass]="theme === 'dark' ? 'fa-sun' : 'fa-moon'"
              aria-hidden="true"
            ></i>
            {{ theme === 'dark' ? 'Light' : 'Dark' }}
          </button>
          <button
            class="fsc-button"
            type="button"
            (click)="refresh()"
            [disabled]="currentLoading || busy || (isContactTab && !contactState.contact)"
          >
            <i class="fa-solid fa-rotate" [class.fa-spin]="currentLoading" aria-hidden="true"></i>
            Refresh
          </button>
          <button
            class="fsc-button fsc-primary"
            type="button"
            (click)="newRecord()"
            [disabled]="!canEdit || busy || (isContactTab && !contactState.contact)"
          >
            <i class="fa-solid fa-plus" aria-hidden="true"></i>
            {{
              isContactTab
                ? 'New rule'
                : tab === 'defaults'
                  ? 'New default'
                  : tab === 'schedules'
                    ? 'New schedule'
                    : tab === 'bases'
                      ? 'New basis'
                      : 'New index value'
            }}
          </button>
        </div>
      </header>

      <div class="grid gap-4 md:grid-cols-2" *ngIf="tab === 'defaults'">
        <article class="fsc-panel p-5" *ngFor="let areaOption of areas">
          <div class="flex items-center justify-between gap-2">
            <h2 class="m-0 text-sm font-semibold">
              {{ areaOption.label }}
              <span class="fsc-muted">/ {{ source(areaOption.value) }}</span>
            </h2>
            <span class="fsc-badge" [class.fsc-positive]="currentDefault(areaOption.value)">
              {{
                loading['defaults']
                  ? 'Loading…'
                  : errors['defaults']
                    ? 'Refresh needed'
                    : currentDefault(areaOption.value)
                      ? 'Current'
                      : 'No current default'
              }}
            </span>
          </div>
          <ng-container *ngIf="currentDefault(areaOption.value) as current; else noDefault">
            <div class="mt-5 grid grid-cols-3 gap-3">
              <div *ngFor="let load of loads">
                <div class="fsc-muted text-xs">{{ loadLabel(load) }}</div>
                <div class="mt-1 text-xl font-bold tabular-nums lg:text-2xl">
                  {{ defaultValue(current, load) }}
                </div>
              </div>
            </div>
            <div class="fsc-muted mt-4 text-xs">{{ dateRange(current) }}</div>
          </ng-container>
          <ng-template #noDefault>
            <p class="fsc-muted mb-0 mt-5 text-sm">
              {{
                loading['defaults']
                  ? 'Loading current defaults…'
                  : 'No active default for today. Add one or review its effective dates.'
              }}
            </p>
          </ng-template>
        </article>
      </div>

      <nav class="fsc-panel flex flex-wrap gap-1 p-1.5" aria-label="Fuel surcharge sections">
        <button
          *ngFor="let item of tabs"
          type="button"
          class="fsc-tab"
          [class.is-selected]="tab === item.id"
          [attr.aria-pressed]="tab === item.id"
          (click)="setTab(item.id)"
          [disabled]="saving"
        >
          <i class="fa-solid" [ngClass]="item.icon" aria-hidden="true"></i>
          {{ item.label }}
        </button>
      </nav>

      <section *ngIf="isContactTab" class="fsc-panel grid items-center gap-4 p-4 md:grid-cols-2">
        <div class="relative">
          <label class="fsc-label" for="fsc-contact">Find a {{ side }}</label>
          <div class="flex gap-2">
            <input
              id="fsc-contact"
              class="fsc-input min-w-0 flex-1"
              [(ngModel)]="contactQuery"
              (ngModelChange)="searchContact()"
              (focus)="searchOpen = true"
              (keydown.escape)="searchOpen = false"
              (keydown.enter)="contactOptions.length === 1 && chooseContact(contactOptions[0])"
              [disabled]="busy"
              placeholder="Type at least 3 characters"
              autocomplete="off"
              [attr.aria-expanded]="searchOpen"
              aria-controls="fsc-contact-options"
            />
            <button type="button" class="fsc-button" (click)="searchContact()" [disabled]="busy">
              Search
            </button>
          </div>
          <div
            id="fsc-contact-options"
            *ngIf="searchOpen && (contactQuery.length >= 3 || contactOptions.length)"
            class="fsc-options"
          >
            <p *ngIf="searching" class="fsc-muted m-0 p-3 text-sm" role="status">Searching…</p>
            <p *ngIf="contactSearchError" class="fsc-error m-0 p-3 text-sm" role="alert">
              {{ contactSearchError }}
            </p>
            <button
              *ngFor="let contact of contactOptions"
              type="button"
              (click)="chooseContact(contact)"
            >
              {{ contact.label }}
            </button>
            <p
              *ngIf="!searching && !contactSearchError && !contactOptions.length"
              class="fsc-muted m-0 p-3 text-sm"
            >
              No contacts found.
            </p>
          </div>
        </div>
        <div>
          <div class="fsc-eyebrow">
            {{ side === 'customer' ? 'Customer charges' : 'Carrier costs' }}
          </div>
          <strong class="mt-1 block text-sm">
            {{ contactState.contact?.label || 'Select a contact to view their FSC rules' }}
          </strong>
          <p class="fsc-muted m-0 mt-1 text-xs">
            Fixed percentage, default multiplier, or an index-based schedule.
          </p>
        </div>
      </section>

      <section
        *ngIf="isContactTab"
        class="fsc-panel fsc-side-rates-panel min-w-0 overflow-hidden"
        [attr.aria-busy]="sideRateState.loading"
      >
        <div class="flex flex-wrap items-center justify-between gap-3 p-4">
          <div>
            <h2 class="m-0 text-base font-semibold">
              Most Recent {{ side === 'customer' ? 'Customer' : 'Carrier' }} FSC Rates
              <span class="fsc-muted text-sm font-normal">
                {{ sideRateRows.length }} {{ sideRateRows.length === 1 ? 'record' : 'records' }}
              </span>
            </h2>
          </div>
          <button
            class="fsc-button fsc-small"
            type="button"
            (click)="loadSideRates(side)"
            [disabled]="sideRateState.loading || busy"
          >
            <i
              class="fa-solid fa-rotate"
              [class.fa-spin]="sideRateState.loading"
              aria-hidden="true"
            ></i>
            Refresh
          </button>
        </div>
        <div *ngIf="sideRateState.loading" class="fsc-loading" role="status">
          Loading {{ side === 'customer' ? 'customers' : 'carriers' }}...
        </div>
        <div *ngIf="sideRateState.error" class="fsc-alert m-4" role="alert">
          {{ sideRateState.error }}
          <button
            type="button"
            class="fsc-button"
            (click)="loadSideRates(side)"
            [disabled]="sideRateState.loading"
          >
            Retry
          </button>
        </div>
        <div
          class="fsc-table-wrap"
          *ngIf="!sideRateState.loading && !sideRateState.error && sideRateRows.length"
        >
          <table class="fsc-table">
            <thead>
              <tr>
                <th>{{ sideNameHeader() }}</th>
                <th>Effective Start</th>
                <th>Effective End</th>
                <th *ngFor="let load of loads">{{ loadLabel(load) }}</th>
                <th>Status</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of sideRateRows">
                <td>
                  <button
                    *ngIf="canCreateRuleFromSideRate(row); else plainSideContactName"
                    type="button"
                    class="fsc-contact-link"
                    (dblclick)="createRuleFromSideRate(row)"
                    [attr.aria-label]="'Create a new FSC rule for ' + sideContactName(row)"
                    [title]="'Double-click to create a new FSC rule for ' + sideContactName(row)"
                  >
                    <strong>{{ sideContactName(row) }}</strong>
                  </button>
                  <ng-template #plainSideContactName>
                    <strong>{{ sideContactName(row) }}</strong>
                  </ng-template>
                </td>
                <td class="whitespace-nowrap">{{ datePart(row.effective_start, 'Any start') }}</td>
                <td class="whitespace-nowrap">{{ datePart(row.effective_end, 'No end date') }}</td>
                <td *ngFor="let load of loads" class="tabular-nums">
                  {{ rateValue(row, load) }}
                </td>
                <td>
                  <ng-container
                    *ngTemplateOutlet="statusBadge; context: { $implicit: row.active }"
                  ></ng-container>
                </td>
                <td class="fsc-note-cell">{{ rateNotes(row) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          *ngIf="!sideRateState.loading && !sideRateState.error && !sideRateRows.length"
          class="fsc-empty fsc-empty--compact"
        >
          <i class="fa-solid fa-table-list" aria-hidden="true"></i>
          <h3>No {{ side === 'customer' ? 'customer' : 'carrier' }} FSC rows found</h3>
        </div>
      </section>

      <div *ngIf="currentError" class="fsc-alert" role="alert">
        {{ currentError }}
        <button type="button" class="fsc-button" (click)="refresh()" [disabled]="currentLoading">
          Retry
        </button>
      </div>
      <p *ngIf="detailLoading" class="fsc-muted m-0 text-sm" role="status">
        Loading schedule details…
      </p>
      <div class="fsc-workspace" [class.has-editor]="editor">
        <section class="fsc-panel min-w-0 overflow-hidden" [attr.aria-busy]="currentLoading">
          <div class="flex flex-wrap items-center justify-between gap-3 p-4">
            <div>
              <h2 class="m-0 text-base font-semibold">
                {{ activeTitle }}
                <span class="fsc-muted text-sm font-normal">
                  {{ filteredCount }} {{ filteredCount === 1 ? 'record' : 'records' }}
                </span>
              </h2>
              <p *ngIf="tab === 'index-values'" class="fsc-muted m-0 mt-1 text-xs">
                Most recent 500 values. Shared values may require an administrator to edit.
              </p>
            </div>
            <div class="flex flex-wrap gap-2">
              <input
                class="fsc-input fsc-filter"
                type="search"
                aria-label="Filter records"
                placeholder="Filter records…"
                [(ngModel)]="filter"
                (ngModelChange)="filterChanged()"
              />
              <select
                *ngIf="tab !== 'bases'"
                class="fsc-input"
                aria-label="Activity filter"
                [(ngModel)]="status"
                (ngModelChange)="filterChanged()"
              >
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div *ngIf="currentLoading" class="fsc-loading" role="status">
            Loading {{ activeTitle.toLowerCase() }}…
          </div>
          <div *ngIf="isContactTab && !contactState.contact" class="fsc-empty">
            <i class="fa-solid fa-address-book" aria-hidden="true"></i>
            <h3>Select a {{ side }}</h3>
            <p>Search above to load their rules and rate history.</p>
          </div>
          <ng-container *ngIf="!currentLoading && (!isContactTab || contactState.contact)">
            <div class="fsc-table-wrap" *ngIf="tab === 'defaults' && defaultRows.length">
              <table class="fsc-table">
                <thead>
                  <tr>
                    <th>Area / source</th>
                    <th>Effective dates</th>
                    <th *ngFor="let load of loads">{{ loadLabel(load) }}</th>
                    <th>Status</th>
                    <th><span class="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let row of defaultRows">
                    <td>
                      <strong>{{ area(row.effective_area) }}</strong>
                      <div class="fsc-muted text-xs">{{ source(row.rate_source) }}</div>
                    </td>
                    <td class="whitespace-nowrap">{{ dateRange(row) }}</td>
                    <td *ngFor="let load of loads" class="tabular-nums">
                      {{ defaultValue(row, load) }}
                    </td>
                    <td>
                      <ng-container
                        *ngTemplateOutlet="statusBadge; context: { $implicit: row.active }"
                      ></ng-container>
                    </td>
                    <td>
                      <button
                        class="fsc-button fsc-small"
                        type="button"
                        (click)="editDefault(row)"
                        [disabled]="busy"
                      >
                        {{ canEdit ? 'Edit' : 'View' }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="fsc-table-wrap" *ngIf="isContactTab && rateRows.length">
              <table class="fsc-table">
                <thead>
                  <tr>
                    <th>Calculation</th>
                    <th>Effective dates</th>
                    <th *ngFor="let load of loads">{{ loadLabel(load) }}</th>
                    <th>Status</th>
                    <th><span class="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  <ng-container *ngFor="let row of rateRows">
                    <tr>
                      <td>
                        <button
                          *ngIf="modeOf(row, side) === 'schedule'; else modeLabel"
                          type="button"
                          class="fsc-expand"
                          (click)="toggleRate(row)"
                          [attr.aria-expanded]="expandedRate === rateId(row, side)"
                        >
                          <i
                            class="fa-solid"
                            [ngClass]="
                              expandedRate === rateId(row, side)
                                ? 'fa-chevron-down'
                                : 'fa-chevron-right'
                            "
                            aria-hidden="true"
                          ></i>
                          Schedule
                        </button>
                        <ng-template #modeLabel>
                          <strong>
                            {{
                              modeOf(row, side) === 'fixed'
                                ? 'Fixed percentage'
                                : 'Default × multiplier'
                            }}
                          </strong>
                          <div *ngIf="modeOf(row, side) === 'offset'" class="fsc-muted text-xs">
                            {{ source(row.offset_basis) }}
                          </div>
                        </ng-template>
                      </td>
                      <td class="whitespace-nowrap">{{ dateRange(row) }}</td>
                      <td *ngFor="let load of loads" class="tabular-nums">
                        {{ rateValue(row, load) }}
                      </td>
                      <td>
                        <ng-container
                          *ngTemplateOutlet="statusBadge; context: { $implicit: row.active }"
                        ></ng-container>
                      </td>
                      <td>
                        <button
                          type="button"
                          class="fsc-button fsc-small"
                          (click)="editRate(row)"
                          [disabled]="busy"
                        >
                          {{ canEdit ? 'Edit' : 'View' }}
                        </button>
                      </td>
                    </tr>
                    <tr *ngIf="expandedRate === rateId(row, side)">
                      <td colspan="7" class="fsc-nested">
                        <div class="flex flex-wrap items-center justify-between gap-2 mb-3">
                          <div>
                            <strong>Available schedules</strong>
                            <p class="fsc-muted m-0 mt-1 text-xs">
                              Specific-rule, contact and shared schedules. Load type and effective
                              dates determine which applies.
                            </p>
                          </div>
                          <button
                            class="fsc-button fsc-small"
                            type="button"
                            (click)="editSchedule(undefined, row)"
                            [disabled]="!canEdit || busy"
                          >
                            Add schedule for this rule
                          </button>
                        </div>
                        <p *ngIf="loading['schedules']" role="status">Loading schedules…</p>
                        <div *ngIf="errors['schedules']" class="fsc-alert" role="alert">
                          {{ errors['schedules'] }}
                          <button class="fsc-button" type="button" (click)="loadSchedules()">
                            Retry
                          </button>
                        </div>
                        <ng-container
                          *ngTemplateOutlet="
                            scheduleList;
                            context: { $implicit: schedulesFor(row) }
                          "
                        ></ng-container>
                        <p
                          *ngIf="
                            !loading['schedules'] &&
                            !errors['schedules'] &&
                            !schedulesFor(row).length
                          "
                          class="fsc-muted text-sm"
                        >
                          No schedule is linked or shared with this rule yet. Add one, then add its
                          brackets.
                        </p>
                      </td>
                    </tr>
                  </ng-container>
                </tbody>
              </table>
            </div>
            <div *ngIf="tab === 'schedules'" class="px-4 pb-4">
              <ng-container
                *ngTemplateOutlet="scheduleList; context: { $implicit: scheduleRows }"
              ></ng-container>
            </div>
            <div class="fsc-table-wrap" *ngIf="tab === 'index-values' && indexRows.length">
              <table class="fsc-table">
                <thead>
                  <tr>
                    <th>Basis / area</th>
                    <th>Effective dates</th>
                    <th>Index value</th>
                    <th>Source</th>
                    <th>Scope</th>
                    <th>Status</th>
                    <th><span class="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let row of indexRows">
                    <td>
                      <strong>{{ basis(row.schedule_basis) }}</strong>
                      <div class="fsc-muted text-xs">{{ area(row.effective_area) }}</div>
                    </td>
                    <td class="whitespace-nowrap">{{ dateRange(row) }}</td>
                    <td class="font-semibold tabular-nums">{{ number(row.index_value) }}</td>
                    <td>{{ row.source_name || '—' }}</td>
                    <td>{{ row.domain_uuid ? 'Organization' : 'Shared' }}</td>
                    <td>
                      <ng-container
                        *ngTemplateOutlet="statusBadge; context: { $implicit: row.active }"
                      ></ng-container>
                    </td>
                    <td>
                      <button
                        class="fsc-button fsc-small"
                        type="button"
                        (click)="editIndex(row)"
                        [disabled]="busy"
                      >
                        {{ canEdit ? 'Edit' : 'View' }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="fsc-table-wrap" *ngIf="tab === 'bases' && basisRows.length">
              <table class="fsc-table">
                <thead>
                  <tr>
                    <th>Basis code</th>
                    <th>Display name</th>
                    <th><span class="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let row of basisRows">
                    <td class="font-mono">{{ row.schedule_basis }}</td>
                    <td>{{ row.label || '—' }}</td>
                    <td>
                      <button
                        class="fsc-button fsc-small"
                        type="button"
                        (click)="editBasis(row)"
                        [disabled]="busy"
                      >
                        {{ canEdit ? 'Edit' : 'View' }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div *ngIf="!filteredCount && !currentError" class="fsc-empty">
              <i class="fa-solid fa-filter-circle-xmark" aria-hidden="true"></i>
              <h3>{{ filter || status !== 'all' ? 'No matching records' : 'No records yet' }}</h3>
              <p>
                {{
                  filter || status !== 'all'
                    ? 'Try a different filter or show all statuses.'
                    : 'Use the New button to add the first record.'
                }}
              </p>
            </div>
          </ng-container>
          <footer *ngIf="filteredCount > pageSize" class="fsc-pagination">
            <span>Page {{ page + 1 }} of {{ pageCount }}</span>
            <div class="flex gap-2">
              <button
                class="fsc-button fsc-small"
                type="button"
                (click)="changePage(-1)"
                [disabled]="page === 0"
              >
                Previous
              </button>
              <button
                class="fsc-button fsc-small"
                type="button"
                (click)="changePage(1)"
                [disabled]="page + 1 >= pageCount"
              >
                Next
              </button>
            </div>
          </footer>
        </section>

        <aside *ngIf="editor" class="fsc-panel fsc-editor" aria-label="FSC editor">
          <div class="flex items-start justify-between gap-3 p-4">
            <div>
              <div class="fsc-eyebrow">{{ editor.id ? 'Existing record' : 'Create record' }}</div>
              <h2 class="m-0 mt-1 text-lg font-bold">{{ editor.title }}</h2>
              <p *ngIf="editor.schedule" class="fsc-muted m-0 mt-1 text-xs">
                {{ editor.schedule.schedule_name }}
              </p>
            </div>
            <button
              type="button"
              class="fsc-button fsc-small"
              (click)="closeEditor()"
              [disabled]="saving"
              aria-label="Close editor"
            >
              <i class="fa-solid fa-xmark" aria-hidden="true"></i>
            </button>
          </div>
          <form [formGroup]="form" (ngSubmit)="save()" class="px-4 pb-4" novalidate>
            <p
              *ngIf="editor.kind === 'default' || (editor.kind === 'rate' && mode === 'fixed')"
              class="fsc-hint"
            >
              Enter percentages, e.g. 25 for 25%. Blank leaves a load type unconfigured; 0 is an
              explicit zero rate.
            </p>
            <p *ngIf="editor.kind === 'rate' && mode === 'offset'" class="fsc-hint">
              The default percentage is multiplied by this value. For example, 0.8 × a 20% default
              gives 16%.
            </p>
            <p *ngIf="editor.kind === 'rate' && mode === 'schedule'" class="fsc-hint">
              Save this rule, expand its row, and add a schedule with brackets for each applicable
              load type.
            </p>
            <p *ngIf="editor.kind === 'schedule'" class="fsc-hint">
              Save the schedule, then expand it to manage its brackets. Each bracket is saved
              separately.
            </p>
            <div class="fsc-editor-fields">
              <label
                *ngFor="let field of visibleFields"
                class="fsc-field"
                [class.fsc-wide]="
                  field.type === 'textarea' ||
                  field.key === 'schedule_name' ||
                  field.key === 'contact_uuid' ||
                  field.key === 'rate_uuid'
                "
              >
                <span>
                  {{ fieldLabel(field) }}
                  <span *ngIf="field.required" class="fsc-muted">*</span>
                </span>
                <select
                  *ngIf="field.type === 'select'"
                  class="fsc-input"
                  [formControlName]="field.key"
                  [attr.aria-invalid]="isInvalid(field.key)"
                >
                  <option *ngFor="let option of field.options" [ngValue]="option.value">
                    {{ option.label }}
                  </option>
                </select>
                <textarea
                  *ngIf="field.type === 'textarea'"
                  class="fsc-input"
                  [formControlName]="field.key"
                  rows="3"
                ></textarea>
                <input
                  *ngIf="field.type !== 'select' && field.type !== 'textarea'"
                  class="fsc-input"
                  [type]="field.type"
                  [formControlName]="field.key"
                  [attr.min]="field.min ?? null"
                  [attr.step]="field.step || null"
                  [attr.aria-invalid]="isInvalid(field.key)"
                />
                <small *ngIf="fieldHelp(field) as help" class="fsc-muted">{{ help }}</small>
                <small *ngIf="isInvalid(field.key)" class="fsc-error">
                  {{
                    field.required &&
                    !form.controls[field.key].value &&
                    form.controls[field.key].value !== 0
                      ? 'This field is required.'
                      : 'Enter a valid value.'
                  }}
                </small>
              </label>
            </div>
            <label *ngIf="editor.kind !== 'basis'" class="mt-4 flex items-center gap-2 text-sm">
              <input type="checkbox" formControlName="active" />
              Active
            </label>
            <p *ngIf="!canEdit" class="fsc-muted text-sm">
              Your account has view access. Editing requires Rate Manager edit permission.
            </p>
            <div *ngIf="editorError" class="fsc-alert mt-4" role="alert">{{ editorError }}</div>
            <div *ngIf="pendingDeactivate" class="fsc-alert mt-4">
              <p class="m-0">
                {{
                  editor.kind === 'basis'
                    ? 'Remove this basis? The API only allows removal when it is unused.'
                    : 'Deactivate this saved record? Your unsaved edits will be discarded.'
                }}
              </p>
              <div class="mt-3 flex gap-2">
                <button
                  type="button"
                  class="fsc-button fsc-danger"
                  (click)="deactivate()"
                  [disabled]="saving"
                >
                  {{ saving ? 'Working…' : 'Confirm' }}
                </button>
                <button
                  type="button"
                  class="fsc-button"
                  (click)="pendingDeactivate = false"
                  [disabled]="saving"
                >
                  Cancel
                </button>
              </div>
            </div>
            <div class="fsc-editor-actions">
              <button
                *ngIf="editor.id && canDelete"
                class="fsc-button fsc-danger"
                type="button"
                (click)="pendingDeactivate = true"
                [disabled]="saving"
              >
                {{ editor.kind === 'basis' ? 'Remove' : 'Deactivate' }}
              </button>
              <span class="flex-1"></span>
              <button class="fsc-button" type="button" (click)="closeEditor()" [disabled]="saving">
                Cancel
              </button>
              <button
                class="fsc-button fsc-primary"
                type="submit"
                [disabled]="!canEdit || saving || pendingDeactivate"
              >
                {{ saving ? 'Saving…' : 'Save' }}
              </button>
            </div>
          </form>
        </aside>
      </div>
    </div>
  </div>

  <ng-template #statusBadge let-value>
    <span class="fsc-badge" [class.fsc-positive]="active(value)">
      {{ active(value) ? 'Active' : 'Inactive' }}
    </span>
  </ng-template>
  <ng-template #scheduleList let-rows>
    <article *ngFor="let schedule of rows" class="fsc-schedule">
      <div class="flex flex-wrap items-center justify-between gap-3 p-3">
        <button
          type="button"
          class="fsc-expand min-w-0 flex-1 text-left"
          (click)="toggleSchedule(schedule)"
          [attr.aria-expanded]="expandedSchedules.has(schedule.fuel_surcharge_schedule_uuid)"
        >
          <i
            class="fa-solid"
            [ngClass]="
              expandedSchedules.has(schedule.fuel_surcharge_schedule_uuid)
                ? 'fa-chevron-down'
                : 'fa-chevron-right'
            "
            aria-hidden="true"
          ></i>
          <span>
            <strong>{{ schedule.schedule_name }}</strong>
            <span class="fsc-muted mt-1 block text-xs font-normal">
              {{ schedule.side | titlecase }} · {{ schedule.load_type }} ·
              {{ basis(schedule.schedule_basis) }} · {{ schedule.currency_type || '—' }}
            </span>
          </span>
        </button>
        <span class="fsc-badge">{{ scheduleScope(schedule) }}</span>
        <ng-container
          *ngTemplateOutlet="statusBadge; context: { $implicit: schedule.active }"
        ></ng-container>
        <button
          class="fsc-button fsc-small"
          type="button"
          (click)="editSchedule(schedule)"
          [disabled]="busy"
        >
          {{ canEdit ? 'Edit' : 'View' }}
        </button>
      </div>
      <div class="fsc-muted px-3 pb-3 text-xs">
        {{ dateRange(schedule) }} · {{ schedule.bracket_count || 0 }} active brackets ·
        {{ area(schedule.effective_area) }}
      </div>
      <div
        *ngIf="expandedSchedules.has(schedule.fuel_surcharge_schedule_uuid)"
        class="fsc-brackets"
      >
        <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
          <strong class="text-sm">Index brackets</strong>
          <div class="flex gap-2">
            <button
              class="fsc-button fsc-small"
              type="button"
              (click)="loadBrackets(schedule)"
              [disabled]="bracketStates[schedule.fuel_surcharge_schedule_uuid]?.loading"
            >
              Refresh brackets
            </button>
            <button
              class="fsc-button fsc-small"
              type="button"
              (click)="editBracket(schedule)"
              [disabled]="
                !canEdit ||
                busy ||
                bracketStates[schedule.fuel_surcharge_schedule_uuid]?.loading ||
                !!bracketStates[schedule.fuel_surcharge_schedule_uuid]?.error
              "
            >
              Add bracket
            </button>
          </div>
        </div>
        <ng-container *ngIf="bracketStates[schedule.fuel_surcharge_schedule_uuid] as state">
          <p *ngIf="state.loading" class="fsc-muted text-sm" role="status">Loading brackets…</p>
          <div *ngIf="state.error" class="fsc-alert" role="alert">{{ state.error }}</div>
          <div *ngIf="!state.loading && state.rows.length" class="fsc-table-wrap">
            <table class="fsc-table fsc-bracket-table">
              <thead>
                <tr>
                  <th>Minimum</th>
                  <th>Maximum</th>
                  <th>{{ schedule.currency_type }} rate</th>
                  <th>Sort</th>
                  <th>Status</th>
                  <th><span class="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let bracket of state.rows">
                  <td>{{ number(bracket.min_index) }}</td>
                  <td>{{ bracket.max_index === null ? 'No limit' : number(bracket.max_index) }}</td>
                  <td class="font-semibold">
                    {{ number(bracket.rate_amount) }}
                    <span class="fsc-muted block text-xs font-normal">
                      {{
                        bracket.rate_unit === 'PER_MILE'
                          ? 'per mile'
                          : bracket.rate_unit === 'FLAT' ||
                              bracket.rate_unit === 'DOLLARS' ||
                              bracket.rate_unit === 'AMOUNT'
                            ? 'flat amount'
                            : bracket.rate_unit
                      }}
                    </span>
                  </td>
                  <td>{{ bracket.sort_order }}</td>
                  <td>
                    <ng-container
                      *ngTemplateOutlet="statusBadge; context: { $implicit: bracket.active }"
                    ></ng-container>
                  </td>
                  <td>
                    <button
                      class="fsc-button fsc-small"
                      type="button"
                      (click)="editBracket(schedule, bracket)"
                      [disabled]="busy"
                    >
                      {{ canEdit ? 'Edit' : 'View' }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p *ngIf="!state.loading && !state.error && !state.rows.length" class="fsc-muted text-sm">
            No brackets yet. Add an index range and its surcharge rate.
          </p>
        </ng-container>
      </div>
    </article>
  </ng-template>
</app-menu>
